import type {
  Case,
  CaseOutcome,
  CaseState,
  FMEAUpdateReview,
  RecommendedAction,
  Alarm,
  ReviewSeat,
} from '@/versions/v1/schema/case';

export function caseReplayFixtures(cases: readonly Case[]): string[] {
  return [...new Set(cases.flatMap((row) => row.analysisRun ? [row.analysisRun.fixture] : []))];
}

export function recordedInvestigationCase(cases: readonly Case[]): Case | undefined {
  return cases.find((row) => row.analysisRun?.recordedAgent);
}
import { ALARM_DISPLAY_SEVERITY } from '@/versions/v1/schema/case';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type { IssueSeverity } from '@/versions/v1/schema/advisory-issue';
import type { RulePriority } from '@/versions/v1/schema/diagnostic-rule';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { Persona } from '@/shared/types';
import type { PillProps } from '@/design-system/Pill';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import {
  applyAlarms,
  applyEvidenceToCase,
  applyFmeaReviews,
  applyRecommendedActions,
  INITIAL_ADVISORY_STATE,
  isRuleLive,
} from '@/versions/v1/shared/AdvisoryEngine';
import { diagnose, variationEventsForDiagnosis, type Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { ladderLineForCase, respondByOverdueClock } from '@/versions/v1/shared/ladder.transform';
import { DEFAULT_CURSOR, type SeriesTime } from '@/versions/v1/schema/reading-series';
import { asCaseTime, formatCaseClock } from '@/versions/v1/shared/clock.derive';
import { ruleById } from '@/versions/v1/shared/diagnostic-rule.transform';
import { formatHitCount, likelihood } from '@/versions/v1/shared/RuleSetEngine';
import { computeCapacityStrategies } from '@/versions/v1/shared/CapacityStrategy';
import { DEFAULT_CAPACITY_PLANT_STATE, STRATEGY_APPROVAL_REVIEW_ID, STRATEGY_PRICE_LIMITS } from '@/versions/v1/shared/capacity-seed';
import { defaultSmelterPriceState } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import {
  ALARMS,
  CASE_OUTCOMES,
  CASES,
  CONFIDENCE_BANDS,
  FMEA_UPDATE_REVIEWS,
  RECOMMENDED_ACTIONS,
  VARIATION_EVENTS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { PLANT_ROLES } from '@/versions/v1/shared/bunker-hill-seed';
import { plantPhrase } from '@/versions/v1/shared/project-seed';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { ZERO_TOUCH_UNDIAGNOSED } from './branch-labels';

export type RoleLens = 'met-process' | 'mech-elec' | 'shift' | 'plant';

export type StageFilter = 'all' | 'open' | 'in-review' | 'closed';

export type AdvisoryTab = 'queue' | 'approvals' | 'alarms' | 'rule-set' | 'driver-tree';

const ADVISORY_TABS: readonly AdvisoryTab[] = ['queue', 'approvals', 'alarms', 'rule-set', 'driver-tree'];

/** Guards a raw path/query param (e.g. the `:tab` route segment or `?tab=`)
 *  against the closed `AdvisoryTab` set — an unknown or missing value is
 *  never a valid tab (fixer round 1 MUST-FIX 3). */
export function isAdvisoryTab(value: string | undefined | null): value is AdvisoryTab {
  return !!value && (ADVISORY_TABS as readonly string[]).includes(value);
}

import {
  approvalTabForReview,
  type ApprovalTab,
} from '@/versions/v1/shared/RuleSetEngine';

export type { ApprovalTab };
export { approvalTabForReview };

export const APPROVAL_TABS: { id: ApprovalTab; label: string }[] = [
  { id: 'to-review', label: 'To review' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

export type EnrichedFmeaReview = {
  review: FMEAUpdateReview;
  outcome?: CaseOutcome;
  caseItem?: Case;
  rule?: DiagnosticRule;
  immediateResponse?: string;
  responsible?: string;
  routedToLabel: string;
  /** `money` marks a basis row that prints US$ (a floor seat does not read it). */
  approvalBasis?: readonly { label: string; value: string; money?: boolean }[];
};

export const ROLE_LENS_TABS: { id: RoleLens; label: string }[] = [
  { id: 'met-process', label: 'met/process' },
  { id: 'mech-elec', label: 'mech-elec' },
  { id: 'shift', label: 'shift' },
  { id: 'plant', label: 'plant' },
];

export const STAGE_FILTER_TABS: { id: StageFilter; label: string }[] = [
  { id: 'all', label: 'all' },
  { id: 'open', label: 'open' },
  { id: 'in-review', label: 'in review' },
  { id: 'closed', label: 'closed' },
];

export const ADVISORY_SCENE_TABS: { id: AdvisoryTab; label: string }[] = [
  { id: 'queue', label: 'Queue' },
  { id: 'driver-tree', label: 'Driver tree' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'alarms', label: 'Alarms' },
  { id: 'rule-set', label: 'Rule set' },
];

/** state → Pill tone (the one new map for this cut). */
export const STATE_CHIP: Record<CaseState, { label: string; tone: PillProps['tone'] }> = {
  detected: { label: 'detected', tone: 'default' },
  matched: { label: 'matched', tone: 'default' },
  routed: { label: 'routed', tone: 'default' },
  'evidence-requested': { label: 'evidence-requested', tone: 'warning' },
  escalated: { label: 'escalated', tone: 'warning' },
  diagnosed: { label: 'diagnosed', tone: 'default' },
  'outcome-documented': { label: 'outcome-documented', tone: 'default' },
  'fmea-review': { label: 'fmea-review', tone: 'warning' },
  approved: { label: 'approved', tone: 'success' },
  rejected: { label: 'rejected', tone: 'danger' },
  closed: { label: 'closed', tone: 'success' },
};

export const BRANCH_CHIP: Record<Case['branch'], { label: string; tone: PillProps['tone']; title?: string }> = {
  'zero-touch': { label: 'zero-touch', tone: 'default', title: ZERO_TOUCH_UNDIAGNOSED },
  ask: { label: 'ask', tone: 'warning' },
  escalate: { label: 'escalate', tone: 'danger' },
};

const SEVERITY_TO_PRIORITY: Record<IssueSeverity, RulePriority> = {
  high: 'P2',
  med: 'P3',
  low: 'P4',
  monitor: 'P4',
};

const SEVERITY_RANK: Record<IssueSeverity, number> = {
  high: 0,
  med: 1,
  low: 2,
  monitor: 3,
};

const LENS_ASSIGNEES: Record<Exclude<RoleLens, 'plant'>, readonly PlantRoleId[]> = {
  'met-process': ['metallurgist', 'superintendent'],
  'mech-elec': ['maintenance'],
  shift: ['ops-supervisor', 'operator', 'cro'],
};

const STAGE_STATES: Record<Exclude<StageFilter, 'all'>, readonly CaseState[]> = {
  open: ['detected', 'matched', 'routed', 'evidence-requested', 'escalated'],
  'in-review': ['diagnosed', 'outcome-documented', 'fmea-review'],
  closed: ['approved', 'rejected', 'closed'],
};

export const ASSIGNEE_SHORT: Record<PlantRoleId, string> = {
  gm: 'GM',
  superintendent: 'Super',
  metallurgist: 'Met',
  'senior-metallurgist': 'Sr Met',
  'ops-supervisor': 'Shift',
  maintenance: 'Maint',
  cro: 'CRO',
  operator: 'Op',
};

const ALERT_TO_CASE_ID: Record<string, string> = {
  'al-zn': 'case-zn-recovery',
  'al-ag': 'case-ag-grade',
  'al-bm': 'case-p80',
  'al-zn2': 'case-zn-ph-response',
  'al-ag2': 'case-ag-grade',
  'al-zn3': 'case-zn-ph-response',
  'al-ag3': 'case-ag-grade',
};

const ISSUE_TO_CASE_ID: Record<string, string> = {
  'ZN-REC': 'case-zn-recovery',
  'AG-CONC': 'case-ag-grade',
  'GRIND-P80': 'case-p80',
  'AS-TREND': 'case-as-trend',
  'HARD-GRIND': 'case-hard',
};

export function advisoryCopy(plantName: string) {
  return {
    title: 'Advisory',
    description: `Case queue, approvals, and alarms for ${plantPhrase(plantName)}.`,
  };
}

export function defaultLensForPersona(persona: Persona): RoleLens {
  switch (persona) {
    case 'Metallurgist':
    case 'Senior metallurgist':
      return 'met-process';
    case 'Maintenance':
      return 'mech-elec';
    case 'Ops supervisor':
    case 'Operator':
    case 'Control room':
      return 'shift';
    case 'GM':
    case 'Superintendent':
      return 'plant';
    default:
      return 'plant';
  }
}

export function priorityFromSeverity(severity: IssueSeverity): RulePriority {
  return SEVERITY_TO_PRIORITY[severity];
}

export function priorityPillTone(priority: RulePriority): PillProps['tone'] {
  return priority === 'P1' || priority === 'P2' ? 'danger' : 'default';
}

export function severityLabel(severity: IssueSeverity): string {
  return severity === 'monitor' ? 'MONITOR' : severity.toUpperCase();
}

const SEVERITY_TONE: Record<IssueSeverity, VizTone> = {
  high: 'bad',
  med: 'warn',
  low: 'info',
  monitor: 'info',
};

const PRIORITY_TONE: Record<Alarm['priority'], VizTone> = {
  P1: 'bad',
  P2: 'bad',
  P3: 'warn',
  P4: 'info',
};

export function severityChip(input: Case | Alarm | { severity: IssueSeverity }): { label: string; tone: VizTone } {
  if ('severity' in input && !('priority' in input) && !('state' in input)) {
    const severity = (input as { severity: IssueSeverity }).severity;
    return {
      label: severityLabel(severity),
      tone: SEVERITY_TONE[severity],
    };
  }
  if ('priority' in input && !('severity' in input)) {
    const alarm = input as Alarm;
    const display = ALARM_DISPLAY_SEVERITY[alarm.priority];
    const deckWord = display === 'high' ? 'HIGH' : display === 'med' ? 'MED' : 'LOW';
    return {
      label: `${alarm.priority} · ${deckWord}`,
      tone: PRIORITY_TONE[alarm.priority],
    };
  }
  const caseItem = input as Case;
  return {
    label: severityLabel(caseItem.severity),
    tone: SEVERITY_TONE[caseItem.severity],
  };
}

/** Slide 05 strip: NOW red · TODAY amber · WEEK blue. */
export function urgencyTone(urgency: RecommendedAction['urgency']): VizTone {
  if (urgency === 'now') return 'bad';
  if (urgency === 'today') return 'warn';
  return 'info';
}

export function confidenceTone(pct: number): VizTone {
  const band = CONFIDENCE_BANDS.find((row) => pct >= row.min);
  return band?.tone ?? 'bad';
}

export function mergedPriorityActions(actions: readonly RecommendedAction[]): RecommendedAction[] {
  return actions
    .filter((action) => action.sequence !== null)
    .slice()
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
}

export function respondByLabel(caseItem: Case, cursor: SeriesTime = DEFAULT_CURSOR): string | null {
  if (caseItem.ladder) {
    const line = ladderLineForCase(caseItem, cursor);
    return line?.text ?? null;
  }
  if (!caseItem.respondBy) return null;
  const escalateLabel = ASSIGNEE_SHORT[caseItem.respondBy.escalatesTo];
  // A plain respond-by past its `at` reads the ladder formatter's own overdue
  // state (`overdue by 10m`), never a second wording (fix #787).
  const overdue = respondByOverdueClock(caseItem, cursor);
  if (overdue) return `${overdue} → ${escalateLabel}`;
  return `respond by ${caseItem.respondBy.at} → ${escalateLabel}`;
}

export function respondByTooltip(caseItem: Case): string | null {
  if (!caseItem.respondBy) return null;
  const escalateLabel = ASSIGNEE_SHORT[caseItem.respondBy.escalatesTo];
  return `no response by ${caseItem.respondBy.at} → ${escalateLabel}`;
}

export function casesWithScenario(
  state: SessionState,
  advisory?: AdvisorySessionState,
  diagnosis?: Diagnosis,
): Case[] {
  const computed = diagnosis?.cases ?? diagnose(state, advisory).cases;
  return [...computed, ...CASES];
}

export function casesWithSession(
  state: SessionState,
  advisory: AdvisorySessionState,
  diagnosis?: Diagnosis,
): Case[] {
  return casesWithScenario(state, advisory, diagnosis)
    .map((caseItem) => applyEvidenceToCase(caseItem, advisory));
}

export function recommendedActionsWithSession(
  advisory: AdvisorySessionState,
): RecommendedAction[] {
  return applyRecommendedActions(RECOMMENDED_ACTIONS, advisory);
}

export function filterCasesByLens(cases: readonly Case[], lens: RoleLens): Case[] {
  if (lens === 'plant') return cases.slice();
  const assignees = LENS_ASSIGNEES[lens];
  return cases.filter((caseItem) => assignees.includes(caseItem.assignedTo));
}

export function filterCasesByStage(cases: readonly Case[], stage: StageFilter): Case[] {
  if (stage === 'all') return cases.slice();
  const states = STAGE_STATES[stage];
  return cases.filter((caseItem) => states.includes(caseItem.state));
}

/** Sort: severity high → monitor, then raisedAt ascending. */
export function sortCases(cases: readonly Case[]): Case[] {
  return cases.slice().sort((a, b) => {
    const rank = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (rank !== 0) return rank;
    return a.raisedAt.localeCompare(b.raisedAt);
  });
}

export function caseById(cases: readonly Case[], caseId: string): Case | undefined {
  return cases.find((caseItem) => caseItem.id === caseId);
}

export function actionsForCase(
  actions: readonly RecommendedAction[],
  caseId: string,
): RecommendedAction[] {
  return allActionsForCase(actions, caseId).filter((a) => a.sequence !== null);
}

/** All recommended actions for one case — card numbered list includes secondary (sequence null) rows. */
export function allActionsForCase(
  actions: readonly RecommendedAction[],
  caseId: string,
): RecommendedAction[] {
  return actions
    .filter((action) => action.caseId === caseId)
    .slice()
    .sort((a, b) => {
      const seqA = a.sequence ?? 999;
      const seqB = b.sequence ?? 999;
      if (seqA !== seqB) return seqA - seqB;
      return a.id.localeCompare(b.id);
    });
}

const URGENCY_RANK: Record<RecommendedAction['urgency'], number> = {
  now: 0,
  today: 1,
  week: 2,
};

export type DrawerActionGroups = {
  pending: RecommendedAction[];
  history: RecommendedAction[];
};

/** Pending first (urgency then sequence); history = done and rejected in decision order. */
export function drawerActionGroups(
  actions: readonly RecommendedAction[],
  caseId: string,
): DrawerActionGroups {
  const all = allActionsForCase(actions, caseId);
  const pending = all
    .filter((a) => a.status === 'proposed')
    .sort((a, b) => {
      const urg = URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency];
      if (urg !== 0) return urg;
      const seqA = a.sequence ?? 999;
      const seqB = b.sequence ?? 999;
      if (seqA !== seqB) return seqA - seqB;
      return a.id.localeCompare(b.id);
    });
  const history = all
    .filter((a) => a.status === 'done' || a.status === 'rejected' || a.status === 'approved')
    .sort((a, b) => {
      const aStamp = a.decision?.at ?? a.implementedAt ?? '';
      const bStamp = b.decision?.at ?? b.implementedAt ?? '';
      return aStamp.localeCompare(bStamp);
    });
  return { pending, history };
}

export function caseIdFromAlertId(alertId: string): string | null {
  return ALERT_TO_CASE_ID[alertId] ?? null;
}

export function caseIdFromIssueId(issueId: string): string | null {
  return ISSUE_TO_CASE_ID[issueId] ?? null;
}

export function caseIdFromCode(code: string): string | null {
  const match = CASES.find((caseItem) => caseItem.code === code);
  return match?.id ?? null;
}

export function assigneeLabel(roleId: PlantRoleId): string {
  return PLANT_ROLES.find((role) => role.id === roleId)?.label ?? roleId;
}

export function variationEventForCase(caseItem: Case, state?: SessionState) {
  if (!caseItem.variationEventId) return undefined;
  if (state) {
    const computed = variationEventsForDiagnosis(diagnose(state)).find(
      (event) => event.id === caseItem.variationEventId,
    );
    if (computed) return computed;
  }
  return VARIATION_EVENTS.find((event) => event.id === caseItem.variationEventId);
}

export function matchedRulesForCase(
  caseItem: Case,
  state?: SessionState,
  advisory: AdvisorySessionState = INITIAL_ADVISORY_STATE,
) {
  const seeded = caseItem.variationEventId
    ? VARIATION_EVENTS.find((event) => event.id === caseItem.variationEventId)
    : undefined;
  if (state && seeded?.variableId) {
    const diagnosis = diagnose(state, advisory);
    if (seeded.subprocessId === 'sp-grind' && state.scenarios.includes('media-depletion')) {
      const media = diagnosis.candidates.find((row) => row.rule.id === 'dr-media');
      if (media) {
        const rule = ruleById(media.rule.id);
        return [
          {
            ruleId: media.rule.id,
            signalPattern: rule?.signalPattern ?? media.rule.signalPattern,
            confidencePct: media.confidencePct,
          },
        ];
      }
    }
    const candidate = diagnosis.candidates.find(
      (row) => row.excursion.variableId === seeded.variableId,
    );
    if (candidate) {
      const rule = ruleById(candidate.rule.id);
      return [
        {
          ruleId: candidate.rule.id,
          signalPattern: rule?.signalPattern ?? candidate.rule.signalPattern,
          confidencePct: candidate.confidencePct,
        },
      ];
    }
  }
  const event = variationEventForCase(caseItem, state);
  if (!event) return [];
  return event.matchedRules.map((match) => {
    const rule = ruleById(match.ruleId);
    return {
      ruleId: match.ruleId,
      signalPattern: rule?.signalPattern ?? match.ruleId,
      confidencePct: match.confidencePct,
    };
  });
}

export function outcomeForCase(caseId: string) {
  return CASE_OUTCOMES.find((outcome) => outcome.caseId === caseId);
}

/** Recently-closed row: outcome sentence and resolved time for the glass. */
export function recentlyClosedCaption(caseItem: Case): string {
  const outcome = outcomeForCase(caseItem.id);
  const action = outcome?.actionTaken ?? caseItem.causeCandidate;
  const when = formatCaseClock(caseItem.resolvedAt ?? caseItem.raisedAt, DEFAULT_CURSOR);
  return `${action} · closed ${when}`;
}

export function reviewsForCase(caseId: string, advisory?: AdvisorySessionState) {
  const outcome = outcomeForCase(caseId);
  if (!outcome) return [];
  const seeded = FMEA_UPDATE_REVIEWS.filter((review) => review.caseOutcomeId === outcome.id);
  if (!advisory || advisory.rulings.length === 0) return seeded;
  return applyFmeaReviews(seeded, advisory);
}

export const CAUSE_CATEGORY_LABEL: Record<string, string> = {
  'metallurgical-process': 'Metallurgical / Process',
  'mechanical-electrical': 'Mechanical / Electrical',
  'spans-both': 'Spans both',
  'feed-ore': 'Feed / ore',
};

export const URGENCY_LABEL: Record<RecommendedAction['urgency'], string> = {
  now: 'NOW',
  today: 'TODAY',
  week: 'WEEK',
};

const CLOSED_CASE_STATES: readonly CaseState[] = ['approved', 'rejected', 'closed'];

/** Axis-7 split: AG-CONC is a Case, not a DCS alarm row. */
const ADVISORY_ALARM_CODES = new Set(['AG-CONC']);

export function alarmsWithSession(advisory: AdvisorySessionState, state: SessionState): Alarm[] {
  return applyAlarms(ALARMS, advisory).map((alarm) => {
    if (alarm.id !== 'alm-bm-brg') return alarm;
    const caseItem = CASES.find((row) => row.alarmId === alarm.id);
    const reading = READINGS.find((row) => row.id === 'rd-bm-brg-temp');
    const warning = reading?.band?.hi;
    const limit = reading?.spec?.hi;
    if (!caseItem || !reading || warning === undefined || limit === undefined) {
      throw new Error('BM-BRG alarm: missing case raise time or Reading thresholds');
    }
    const raisedAt = asCaseTime(caseItem.raisedAt);
    const observed = seriesAt('rs-bm-brg-temp', { ...state, cursor: raisedAt }).latest?.value;
    if (observed === undefined) throw new Error('BM-BRG alarm: no historian sample at raise time');
    return {
      ...alarm,
      message: `Ball mill bearing temp ${observed.toFixed(reading.decimals)} ${reading.unit} (warning ${warning} ${reading.unit} · limit ${limit} ${reading.unit})`,
    };
  });
}

export function dcsAlarmsForPanel(
  alarms: readonly Alarm[],
  options: { hideP4?: boolean; activeOnly?: boolean } = {},
): { visible: Alarm[]; hiddenP4Count: number } {
  const dcs = alarms.filter((alarm) => !ADVISORY_ALARM_CODES.has(alarm.code));
  const hiddenP4 = options.hideP4 ? dcs.filter((alarm) => alarm.priority === 'P4') : [];
  let visible = options.hideP4 ? dcs.filter((alarm) => alarm.priority !== 'P4') : dcs.slice();
  if (options.activeOnly) {
    visible = visible.filter((alarm) => alarm.state === 'active');
  }
  return { visible, hiddenP4Count: hiddenP4.length };
}

/** Alarms with spawned zero-touch response cases (ui-screens §2 zero-touch response). */
export function zeroTouchAlarmResponses(
  alarms: readonly Alarm[],
  cases: readonly Case[],
): { alarm: Alarm; caseItem: Case }[] {
  return alarms
    .filter((alarm) => alarm.spawnedCaseId)
    .flatMap((alarm) => {
      const caseItem = cases.find((row) => row.id === alarm.spawnedCaseId);
      // Only a pure alarm response (no VariationEvent behind it) is the FMEA's
      // Immediate Response Action; a spawned *finding* belongs in the case group.
      return caseItem && !caseItem.variationEventId ? [{ alarm, caseItem }] : [];
    });
}

export function advisoryCasesForPanel(cases: readonly Case[]): Case[] {
  return cases
    // Findings only — cases spawned by a VariationEvent (the deck's four AI findings), not the
    // §6 worked examples or review-only cases; those live in the Advisory queue.
    .filter((caseItem) => caseItem.variationEventId && !CLOSED_CASE_STATES.includes(caseItem.state))
    .slice()
    .sort((a, b) => {
      const rank = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
      if (rank !== 0) return rank;
      return a.raisedAt.localeCompare(b.raisedAt);
    });
}

export function alarmSummaryCaption(alarms: readonly Alarm[]): string {
  const active = alarms.filter((alarm) => alarm.state === 'active').length;
  const highs = alarms.filter((alarm) => ALARM_DISPLAY_SEVERITY[alarm.priority] === 'high').length;
  const meds = alarms.filter((alarm) => ALARM_DISPLAY_SEVERITY[alarm.priority] === 'med').length;
  const lows = alarms.filter((alarm) => ALARM_DISPLAY_SEVERITY[alarm.priority] === 'low').length;
  const parts = [`${alarms.length} alarm${alarms.length === 1 ? '' : 's'}`, `${active} active`];
  if (highs > 0) parts.push(`${highs} High`);
  if (meds > 0) parts.push(`${meds} Med`);
  if (lows > 0) parts.push(`${lows} Low`);
  return parts.join(' · ');
}

export function alarmPriorityLabel(priority: Alarm['priority']): string {
  return priority;
}

export function fmeaReviewsWithSession(advisory: AdvisorySessionState): FMEAUpdateReview[] {
  return applyFmeaReviews(FMEA_UPDATE_REVIEWS, advisory);
}

export function filterReviewsByTab(
  reviews: readonly FMEAUpdateReview[],
  tab: ApprovalTab,
): FMEAUpdateReview[] {
  return reviews.filter((review) => approvalTabForReview(review) === tab);
}

export function enrichFmeaReview(
  review: FMEAUpdateReview,
  cases: readonly Case[],
): EnrichedFmeaReview {
  const outcome = CASE_OUTCOMES.find((row) => row.id === review.caseOutcomeId);
  const caseItem = outcome ? cases.find((row) => row.id === outcome.caseId) : undefined;
  const ruleId = review.proposedChange.ruleId;
  const rule = ruleId ? ruleById(ruleId) : undefined;
  return {
    review,
    outcome,
    caseItem,
    rule,
    immediateResponse: rule?.recommendedAction,
    responsible: rule ? assigneeLabel(rule.responsible as PlantRoleId) : undefined,
    routedToLabel: formatReviewRoutedTo(review.reviewSeats),
    approvalBasis: review.id === STRATEGY_APPROVAL_REVIEW_ID ? strategyApprovalBasis() : undefined,
  };
}

function strategyApprovalBasis(): readonly { label: string; value: string; money?: boolean }[] {
  const plantState = DEFAULT_CAPACITY_PLANT_STATE;
  const read = computeCapacityStrategies({ grade: plantState.grade, prices: defaultSmelterPriceState(), plantState });
  const balanced = read.strategies.find((row) => row.id === 'balanced')!;
  const strategy = read.strategies.find((row) => row.id === 'lead-silver-max')!;
  const deltaVsCurrent = strategy.financials.ebitdaA - read.currentEbitdaA;
  const balancedLead = Math.round(balanced.financials.ebitdaA) - Math.round(strategy.financials.ebitdaA);
  const money = (value: number) => `${CURRENCY_GLYPH}${Math.abs(value).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
  const signedMoney = (value: number) => `${value >= 0 ? '+' : '−'}${money(value)}`;
  const breakeven = read.breakevenAgOz === null
    ? `Balanced leads through ${CURRENCY_GLYPH}${STRATEGY_PRICE_LIMITS.silverMax.toFixed(2)}/oz Ag`
    : `Strategy C leads above ${CURRENCY_GLYPH}${read.breakevenAgOz.toFixed(2)}/oz Ag`;
  return [
    {
      label: 'Setpoint trade-off',
      value: `Zn in Pb concentrate ${strategy.quality.zincInLeadConcentratePct.toFixed(1)}% vs ${strategy.quality.leadConcentrateZnThresholdPct.toFixed(1)}% limit · Zn recovery ${strategy.recoveries.znPct.toFixed(1)}% vs ${balanced.recoveries.znPct.toFixed(1)}% Balanced`,
    },
    {
      label: 'Value at reference prices',
      money: true,
      value: `Strategy C ${money(strategy.financials.ebitdaA)}/day at ${strategy.feedStph.toFixed(1)} stph vs current ${money(read.currentEbitdaA)}/day at ${plantState.feedStph.toFixed(1)} stph (${signedMoney(deltaVsCurrent)}); Balanced leads by ${money(balancedLead)}/day at Ag ${CURRENCY_GLYPH}${read.prices.silverPerOz.toFixed(2)}/oz. ${breakeven}.`,
    },
  ];
}

export function formatReviewRoutedTo(seats: readonly ReviewSeat[]): string {
  if (seats.length === 0) return '';
  const labels = seats.map((seat) => REVIEW_SEAT_LABEL[seat]);
  return `routed to ${labels.join(' · ')}`;
}

export function enrichFmeaReviews(
  reviews: readonly FMEAUpdateReview[],
  cases: readonly Case[],
): EnrichedFmeaReview[] {
  return reviews.map((review) => enrichFmeaReview(review, cases));
}

export function ruleIsLive(ruleId: string, advisory: AdvisorySessionState): boolean {
  return isRuleLive(ruleId, advisory);
}

export function fmeaOccurrenceLabel(
  ruleId: string | undefined,
  cases: readonly Case[],
  state: SessionState,
  diagnosis?: ReturnType<typeof diagnose>,
): { label: string; tooltip?: string } {
  if (!ruleId) return { label: '—' };
  const read = likelihood(ruleId, cases, state, diagnosis);
  const tooltip =
    read.authored !== undefined ? `authored O = ${read.authored}` : undefined;
  return {
    label: formatHitCount(read.hits, read.windowDays),
    tooltip,
  };
}

export const REVIEW_SEAT_LABEL: Record<ReviewSeat, string> = {
  'senior-metallurgist': 'Senior Metallurgist',
  'senior-reliability-engineer': 'Senior Reliability Engineer',
};

/** The plant role a review seat resolves to — same role vocabulary the case handler uses. */
export const REVIEW_SEAT_ROLE: Record<ReviewSeat, PlantRoleId> = {
  'senior-metallurgist': 'senior-metallurgist',
  'senior-reliability-engineer': 'maintenance',
};

/**
 * One actor, one name: renders the reviewer/approver of a trail event the same way the
 * drawer header renders the case handler — `name · Role` — instead of the raw seat id.
 * Falls back to the seat label when no person is attributed yet (still pending).
 */
export function reviewActorLabel(
  actorName: string | undefined,
  seats: readonly ReviewSeat[],
): string {
  const roleLabel = seats.map((seat) => assigneeLabel(REVIEW_SEAT_ROLE[seat])).join(' / ');
  if (actorName) return `${actorName} · ${roleLabel}`;
  return `${roleLabel} seat`;
}

import { inBandDivergences, samplePointForDivergence } from '@/versions/v1/shared/SpcEngine';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export type SampleRequestRow = {
  id: string;
  readingLabel: string;
  direction: 'up' | 'down';
  basisLabel: string;
  pointCount: number;
  detectedAt: string;
  requested: boolean;
  requestedAt?: string;
  requestedByLabel?: string;
  /** Projected from the *current* `state.cadence`, not the mode recorded on
   * the request at click time (SA-F3/SA-F4 fix round 1) -- so a request
   * made under `lab` reads as a campaign the moment the plant switches to
   * `osa`, and back. */
  mode?: 'rush' | 'campaign';
  campaign?: { intervalMin: number; hours: number };
  divergenceId: string;
  samplePointId?: string;
};

export function sampleRequestRows(
  state: SessionState,
  advisory: AdvisorySessionState,
): SampleRequestRow[] {
  const divergences = inBandDivergences(state);
  return divergences.map((divergence) => {
    const reading = readingByIdLookup(divergence.readingId);
    const request = advisory.sampleRequests.get(divergence.id);
    const samplePoint = samplePointForDivergence(divergence);
    const row: SampleRequestRow = {
      id: divergence.id,
      readingLabel: reading?.label ?? divergence.readingId,
      direction: divergence.direction,
      basisLabel: divergence.basisLabel,
      pointCount: divergence.pointCount,
      detectedAt: divergence.detectedAt.split('T')[1] ?? divergence.detectedAt,
      requested: Boolean(request),
      divergenceId: divergence.id,
      samplePointId: samplePoint?.id,
    };
    if (request) {
      row.requestedAt = request.requestedAt.split('T')[1] ?? request.requestedAt;
      row.requestedByLabel = assigneeLabel(request.requestedBy);
      if (state.cadence === 'osa') {
        row.mode = 'campaign';
        // SA-F20: derive from the request's own campaign (the single
        // cadence source, set once in AdvisoryEngine.requestSamples), not a
        // second hard-coded copy here. The literal survives only as the
        // fallback for a lab-mode request being projected under osa, where
        // the request itself never recorded a campaign.
        row.campaign = request.campaign ?? { intervalMin: 30, hours: 4 };
      } else {
        row.mode = 'rush';
      }
    }
    return row;
  });
}
